import { useEffect, useState } from 'react'
import { useEquipmentScope } from './equipmentScope'
import { HoldTimeField, parseHoldTime } from './facility/HoldTimeField'
import { Pencil } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChecklistEditor, newChecklistRow, type ChecklistRow } from './ChecklistEditor'
import { EquipmentDocumentsPanel } from './EquipmentDocumentsPanel'
import { MaintainedField } from './MaintainedField'
import { DefaultAssigneeField } from './DefaultAssigneeField'
import { useAssignableUsers } from '@/hooks/useAssignableUsers'
import {
  DEFAULT_EQUIPMENT_PURPOSE,
  EQUIPMENT_PURPOSE_LABELS,
  type EquipmentPurpose,
} from '@/types/equipment'
import type {
  CalendarInfo,
  EquipmentGroup,
  EquipmentSchedule,
  PreStartChecklistItem,
} from '@/types/equipment'

/** Minimal shape of the equipment type being edited. */
export type EditableEquipmentType = {
  id: string
  name: string
  group: EquipmentGroup
  purpose: EquipmentPurpose
  /** Whether this kind of machine is serviced on a cycle. */
  maintained: boolean
  /** What is configured today, so the dialog can warn before it stops. */
  schedule: EquipmentSchedule
  calendar?: CalendarInfo
  preStartChecklist: PreStartChecklistItem[]
  /** Technician generated PM tasks for this type are assigned to, if any. */
  defaultAssigneeId?: string
  /** Room types: hours a clean holds (Cleaning Visual); null = not set. */
  cleanHoldTimeHours?: number | null
}

export function EditEquipmentTypeModal({
  open,
  onClose,
  type,
  groups,
  existingTypes,
  takenElsewhere = [],
  onSave,
}: {
  open: boolean
  onClose: () => void
  /** The equipment type being edited. */
  type: EditableEquipmentType | null
  /** Group names to choose from (fetched from the API). */
  groups: string[]
  /** Other type names, for duplicate check (excludes the current type's own name). */
  existingTypes: string[]
  /** Type names on the other page (Equipment / Cleaning): names are unique across both. */
  takenElsewhere?: string[]
  onSave: (
    id: string,
    data: {
      name: string
      group: EquipmentGroup
      purpose: EquipmentPurpose
      maintained: boolean
      preStartChecklist: { label: string; required: boolean }[]
      /** '' clears an existing default; a non-empty id sets one. */
      defaultAssigneeId: string
      /** Room types only; null clears it. */
      cleanHoldTimeHours?: number | null
    },
  ) => void
}) {
  const scope = useEquipmentScope()
  const { copy } = scope
  const [name, setName] = useState('')
  const [group, setGroup] = useState<string>('')
  const [purpose, setPurpose] = useState<EquipmentPurpose>(DEFAULT_EQUIPMENT_PURPOSE)
  const [maintained, setMaintained] = useState(true)
  const [checklistRows, setChecklistRows] = useState<ChecklistRow[]>([])
  const [defaultAssigneeId, setDefaultAssigneeId] = useState<string | undefined>(undefined)
  const [holdTime, setHoldTime] = useState('')
  const isRoom = scope.variant === 'room'
  const { users: assignableUsers } = useAssignableUsers()

  useEffect(() => {
    if (open && type) {
      setName(type.name)
      setGroup(type.group)
      setDefaultAssigneeId(type.defaultAssigneeId)
      setPurpose(type.purpose)
      setMaintained(type.maintained)
      setHoldTime(type.cleanHoldTimeHours != null ? String(type.cleanHoldTimeHours) : '')
      const sorted = [...type.preStartChecklist].sort((a, b) => a.order - b.order)
      setChecklistRows(
        sorted.length > 0
          ? sorted.map((item) => ({ key: item.id, label: item.label, required: item.required }))
          : [newChecklistRow('', false)],
      )
    }
  }, [open, type])

  const trimmed = name.trim()
  const isDuplicate = existingTypes.some((t) => t.toLowerCase() === trimmed.toLowerCase())
  const isTakenElsewhere = !isDuplicate && takenElsewhere.some((t) => t.toLowerCase() === trimmed.toLowerCase())
  const holdTimeOk = !isRoom || parseHoldTime(holdTime) !== undefined
  const canSubmit = trimmed !== '' && !isDuplicate && !isTakenElsewhere && group !== '' && holdTimeOk

  const handleSave = () => {
    if (!type || !canSubmit) return
    const preStartChecklist = checklistRows
      .filter((r) => r.label.trim() !== '')
      .map((r) => ({ label: r.label.trim(), required: r.required }))
    onSave(type.id, {
      name: trimmed,
      group: group as EquipmentGroup,
      purpose,
      maintained,
      preStartChecklist,
      defaultAssigneeId: defaultAssigneeId ?? '',
      ...(isRoom ? { cleanHoldTimeHours: parseHoldTime(holdTime) ?? null } : {}),
    })
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-lg'>
        <div className='flex items-start gap-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
            <Pencil className='h-5 w-5' />
          </div>
          <div>
            <h2 className='text-base font-semibold text-gray-900'>{copy.editType}</h2>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              Its schedule is shared by all units of the type.
            </p>
          </div>
        </div>

        <div className='mt-4 space-y-3'>
          <div className='space-y-1'>
            <label className='text-xs font-medium text-gray-600'>{copy.type}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={copy.typeExample}
            />
            {isDuplicate && (
              <p className='text-[11px] text-rose-500'>That {copy.unitLower} type already exists.</p>
            )}
            {isTakenElsewhere && <p className='text-[11px] text-rose-500'>{copy.takenElsewhere}</p>}
          </div>
          <div className='space-y-1'>
            <label className='text-xs font-medium text-gray-600'>{copy.group}</label>
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select a group' />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g} value={g}>
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {scope.choosePurpose && (
          <div className='space-y-1'>
            <label className='text-xs font-medium text-gray-600'>Purpose</label>
            <Select value={purpose} onValueChange={(v) => setPurpose(v as EquipmentPurpose)}>
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scope.purposes.map((p) => (
                  <SelectItem key={p} value={p}>
                    {EQUIPMENT_PURPOSE_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Changing this reclassifies every unit of the type at once, since
                purpose is inherited. Worth saying before the save, not after. */}
            <p className='text-[11px] text-muted-foreground'>
              Applies to every unit of this type.
            </p>
          </div>
          )}

          <MaintainedField
            value={maintained}
            onChange={setMaintained}
            existing={type ?? undefined}
          />

          {isRoom && <HoldTimeField value={holdTime} onChange={setHoldTime} />}

          <DefaultAssigneeField
            value={defaultAssigneeId}
            onChange={setDefaultAssigneeId}
            users={assignableUsers}
          />

          <ChecklistEditor rows={checklistRows} onChange={setChecklistRows} />

          {/*
            Documents are saved as they are uploaded, NOT on "Save Changes" —
            an upload needs the type's id, which only exists once the type does.
            That is also why the Add dialog carries no documents section.
          */}
          {type && <EquipmentDocumentsPanel owner={{ kind: 'type', id: type.id }} />}
        </div>

        <DialogFooter className='mt-5'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSubmit}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
